import React, { useState } from "react";
import { Palette, Check, X } from "lucide-react";
import { useTheme, THEMES } from "@/lib/theme";

export default function ThemeButton({ floating = true, label = false }) {
  const [open, setOpen] = useState(false);
  const { current } = useTheme();
  const cls = floating
    ? "fixed top-4 right-4 z-30 w-10 h-10 rounded-full border shadow-soft"
    : "inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium";
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="theme-switcher-btn"
        title="Change theme"
        className={cls}
        style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
      >
        <Palette className="w-4 h-4 inline" />
        {label && <span className="ml-1">Theme · {current?.name}</span>}
      </button>
      {open && <ThemePickerModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function ThemePickerModal({ onClose }) {
  const { theme, setTheme, showFooter, setShowFooter } = useTheme();
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="theme-picker-modal"
    >
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden border max-h-[90vh] flex flex-col"
        style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div>
            <h3 className="font-display font-semibold text-lg">Choose a theme</h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Updates colors, fonts, navigation, cards and buttons across the entire app.</p>
          </div>
          <button onClick={onClose} data-testid="theme-picker-close"><X className="w-5 h-5" style={{ color: "var(--text-muted)" }} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-5 overflow-y-auto flex-1">
          {Object.values(THEMES).map((t) => {
            const active = t.id === theme;
            return (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                data-testid={`theme-option-${t.id}`}
                className="text-left rounded-xl p-4 border transition-all relative"
                style={{
                  borderColor: active ? "var(--primary)" : "var(--border)",
                  background: t.tokens.surface,
                  color: t.tokens.text,
                  boxShadow: active ? "0 0 0 3px " + t.tokens.primary + "33" : undefined,
                }}
              >
                {active && (
                  <div
                    className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: t.tokens.primary, color: t.tokens["primary-fg"] }}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </div>
                )}
                <div style={{ fontFamily: t.fontDisplay, fontWeight: 700, letterSpacing: "-0.01em" }} className="text-base">
                  {t.name}
                </div>
                <div className="text-xs mt-0.5" style={{ color: t.tokens["text-muted"], fontFamily: t.fontSans }}>
                  {t.tagline}
                </div>

                {/* Preview block */}
                <div className="mt-3 rounded-lg p-3 border" style={{ background: t.tokens["surface-2"], borderColor: t.tokens.border }}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center font-bold" style={{ background: t.tokens.primary, color: t.tokens["primary-fg"], fontFamily: t.fontDisplay }}>
                      Cc
                    </div>
                    <div style={{ fontFamily: t.fontDisplay, fontWeight: 600, color: t.tokens.text }}>OncoCadence</div>
                  </div>
                  <div className="mt-2.5 flex gap-1.5 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: t.tokens.primary, color: t.tokens["primary-fg"], fontFamily: t.fontSans }}>Primary</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: t.tokens.primary, color: t.tokens.primary, fontFamily: t.fontSans }}>Outline</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: t.tokens.border, color: t.tokens["text-muted"], fontFamily: t.fontSans }}>Muted</span>
                  </div>
                </div>

                {/* Swatch strip */}
                <div className="mt-3 flex gap-1">
                  {t.swatches.map((s) => (
                    <div key={s.label} className="flex-1 h-2 rounded-full" style={{ background: s.value, border: s.value === "#FFFFFF" || s.value === "#FAFAF9" ? "1px solid " + t.tokens.border : "none" }} title={`${s.label}: ${s.value}`} />
                  ))}
                </div>
                <div className="mt-1.5 text-[10px] flex justify-between" style={{ color: t.tokens["text-muted"], fontFamily: t.fontMono || t.fontSans }}>
                  {t.swatches.map((s) => <span key={s.label}>{s.label}</span>)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="palette-footer-toggle">
            <input
              type="checkbox"
              checked={showFooter}
              onChange={(e) => setShowFooter(e.target.checked)}
              className="w-4 h-4 rounded"
              style={{ accentColor: "var(--primary)" }}
            />
            <span style={{ color: "var(--text)" }}>Show palette tokens strip in footer</span>
          </label>
          <button
            onClick={onClose}
            data-testid="theme-picker-done"
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
